import React, { useEffect, useRef, useState } from 'react';
import { Field, Flex, Icon, Input, Text } from '@chakra-ui/react';
import { FiClock, FiFileText } from 'react-icons/fi';
import { useGetCustomerQuery, useSendNotificationMutation } from '../../api';
import { ADD_TAX_ID_TEMPLATE_ID } from '../../api/notification/types';
import { useTranslation } from '../../hooks/useTranslation';
import { modalManager } from '../../utils/modalManager';
import { render } from '../../utils/templateUtils';
import {
    canResubmitInn,
    getInnSubmission,
    InnSubmission,
    saveInnSubmission,
} from '../../utils/innSubmission';
import { getTaxIdIntroDelayDays } from '../../utils/taxIdIntro';
import Button from '../base/Button';

interface InnModalContentProps {
    /** Обязательная незакрываемая модалка (после истечения отсрочки на сообщение ИНН) */
    isRequired?: boolean;
}

/**
 * Контент модалки с запросом ИНН: ввод ИНН и отправка его в поддержку письмом.
 *
 * После успешной отправки показывается состояние «ИНН на проверке», а возможность
 * отправить ИНН повторно (с редактированием) появляется через 24 часа.
 */
const InnModalContent: React.FC<InnModalContentProps> = ({ isRequired = false }) => {
    const { t } = useTranslation();
    const { data: customer } = useGetCustomerQuery();
    const [sendNotification, { isLoading }] = useSendNotificationMutation();

    const customerId = customer?.id;
    const [submission, setSubmission] = useState<InnSubmission | null>(() => getInnSubmission(customerId));
    const [taxId, setTaxId] = useState<string>(() => submission?.taxId ?? '');
    const [error, setError] = useState<string | null>(null);
    const loadedCustomerIdRef = useRef<number | undefined>(customerId);

    // Клиент мог еще не загрузиться к моменту монтирования — перечитываем состояние по его id
    useEffect(() => {
        if (customerId === loadedCustomerIdRef.current) return;

        loadedCustomerIdRef.current = customerId;
        const stored = getInnSubmission(customerId);
        setSubmission(stored);
        setTaxId(stored?.taxId ?? '');
    }, [customerId]);

    const isUnderReview = submission !== null;
    const canResubmit = isUnderReview && canResubmitInn(submission);
    // Инпут скрыт, пока ИНН на проверке и не прошли 24 часа с момента отправки
    const isInputVisible = !isUnderReview || canResubmit;

    const handleSubmit = async () => {
        const value = taxId.trim();
        if (!value) {
            setError(t.pages.support.innInputRequired);
            return;
        }

        try {
            await sendNotification({
                template_id: ADD_TAX_ID_TEMPLATE_ID,
                vars: {
                    CompanyName: customer?.company_name ?? '',
                    TaxID: value,
                },
            }).unwrap();

            setError(null);
            setSubmission(saveInnSubmission(customerId, value));
        } catch {
            setError(t.pages.support.innSubmitError);
        }
    };

    const title = isUnderReview
        ? t.pages.support.innPendingTitle
        : isRequired
          ? t.pages.support.innRequiredModalTitle
          : t.pages.support.innModalTitle;

    const message = isUnderReview
        ? render(t.pages.support.innPendingMessage, { tax_id: submission.taxId })
        : isRequired
          ? render(t.pages.support.innRequiredModalMessage, {
                n: getTaxIdIntroDelayDays(customer),
            })
          : t.pages.support.innModalMessage;

    return (
        <Flex direction="column" align="center" textAlign="center" gap={4} py={4} w="full">
            <Flex
                align="center"
                justify="center"
                boxSize="72px"
                borderRadius="full"
                bg={isUnderReview ? 'blue.100' : 'yellow.100'}
            >
                <Icon
                    as={isUnderReview ? FiClock : FiFileText}
                    boxSize={9}
                    color={isUnderReview ? 'blue.600' : 'yellow.600'}
                />
            </Flex>

            <Text fontSize="xl" fontWeight="bold">
                {title}
            </Text>
            <Text color="gray.600">{message}</Text>

            {canResubmit && <Text color="gray.600">{t.pages.support.innPendingResubmitHint}</Text>}

            {isInputVisible && (
                <Field.Root invalid={!!error} w="full" textAlign="left">
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.pages.support.innInputLabel}
                    </Field.Label>
                    <Input
                        type="text"
                        value={taxId}
                        onChange={(e) => {
                            setTaxId(e.target.value);
                            if (error) setError(null);
                        }}
                        placeholder={t.pages.support.innInputPlaceholder}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        colorPalette={error ? 'red' : 'gray'}
                        disabled={isLoading}
                    />
                    {error && (
                        <Field.ErrorText color="red.500" fontSize="sm" mt={1}>
                            {error}
                        </Field.ErrorText>
                    )}
                </Field.Root>
            )}

            {/* Ошибка отправки, когда инпут скрыт (повторная отправка недоступна) */}
            {!isInputVisible && error && (
                <Text color="red.500" fontSize="sm">
                    {error}
                </Text>
            )}

            <Flex
                gap={3}
                w="full"
                mt={2}
                direction={{ base: 'column-reverse', md: 'row' }}
                justifyContent="flex-end"
            >
                {!isRequired && (
                    <Button
                        buttonType="secondary"
                        size="lg"
                        fontSize="lg"
                        w={{ base: 'full', md: 'auto' }}
                        onClick={() => modalManager.remove()}
                    >
                        {isUnderReview
                            ? t.pages.support.innModalCloseButton
                            : t.pages.support.innModalLaterButton}
                    </Button>
                )}
                {isInputVisible && (
                    <Button
                        buttonType="success"
                        size="lg"
                        fontSize="lg"
                        w={{ base: 'full', md: 'auto' }}
                        isLoading={isLoading}
                        onClick={() => {
                            void handleSubmit();
                        }}
                    >
                        {canResubmit
                            ? t.pages.support.innResubmitButton
                            : t.pages.support.innModalSubmitButton}
                    </Button>
                )}
            </Flex>
        </Flex>
    );
};

export default InnModalContent;
